import React from "react";
import { Box, Button, InlineNotification, Modal, Option, useToast, useTranslation } from "@familyzone/component-library";
import { UserSearchSelector } from "../UserSearch/UserSearchSelector";
import LayoutConfig from "../../utils/LayoutConfig";
import { generateMasqueradeCode, isUserTeacher } from "../../utils/api/Users";
import { useSessionStore } from "../../storez/SessionStore";
import { UserOption } from "../../types/Users";

interface MasqueradeClasswizeSignInAsTeacherModalProps {
  teacherGroups: string[];
}

const MasqueradeClasswizeSignInAsTeacherModal: React.FC<MasqueradeClasswizeSignInAsTeacherModalProps> = ({ teacherGroups }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [isTeacher, setIsTeacher] = React.useState(false);
  const [hasChosenUser, setHasChosenUser] = React.useState(false);
  const applianceId = useSessionStore((state) => state.device?.id);

  const { errorToast } = useToast();

  const handleClickSubmit = (selected: Option | null) => {
    if (!selected) return;
    const userOption = selected as UserOption;
    redirectToMasqueradeUrl(selected.value.toString(), userOption.qoriaUserID ?? "");
  };

  const redirectToMasqueradeUrl = (username: string, qoriaUserID: string) => {
    if (!applianceId) {
      errorToast({
        title: t("There was an error masquerading as a teacher."),
        description: t(
          "An error occurred while masquerading as a teacher. Please try again later. If this keeps happening, please contact support."
        ),
      });
      return;
    }
    generateMasqueradeCode(username, qoriaUserID)
      .then((response) => {
        const idParam = qoriaUserID ? `&id=${qoriaUserID}` : "";
        window.open(
          `${LayoutConfig.classwize_link() as string}/login/masquerade/callback?user=${username}&deviceid=${applianceId}&token=${
            response.code
          }${idParam}`,
          "_blank"
        );
      })
      .catch(() => {
        errorToast({
          title: t("There was an error masquerading as a teacher."),
          description: t(
            "An error occurred while masquerading as a teacher. Please try again later. If this keeps happening, please contact support."
          ),
        });
      })
      .finally(() => {
        setOpen(false);
      });
  };

  const validateIsTeacher = async (selected: Option | null) => {
    if (!selected) return;
    try {
      const userOption = selected as UserOption;
      const userIsTeacher = await isUserTeacher(teacherGroups, userOption.qoriaUserID ?? "");
      setIsTeacher(userIsTeacher);
      setHasChosenUser(true);
    } catch {
      errorToast({
        title: t("There was an error checking this user is a teacher."),
        description: t("Please try again later. If this keeps happening, please contact support."),
      });
      setIsTeacher(false);
      setHasChosenUser(false);
    }
  };

  return (
    <>
      <Button variant="primary" onClick={() => setOpen(true)}>
        {t("Sign in as Teacher")}
      </Button>
      <Modal
        headerText={t("Sign in as Teacher")}
        size="md"
        isOpen={open}
        onClose={() => setOpen(false)}
        contentProps={{ style: { overflow: "visible" } }}
      >
        <Box mt="sp24">
          <UserSearchSelector
            placeholderText={t("Search for a user")}
            buttonText={t("Sign in")}
            handleChange={(selected) => void (async () => await validateIsTeacher(selected))()}
            handleSubmit={handleClickSubmit}
            submitDisabled={!isTeacher}
          />
          {hasChosenUser && !isTeacher && (
            <Box mt="sp16">
              <InlineNotification
                notificationTitle={t("Cannot sign in as this user.")}
                notificationDescription={t("The selected user is not a teacher of the selected teacher groups.")}
                status="error"
                isRemovable
              />
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default MasqueradeClasswizeSignInAsTeacherModal;
